import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Sparkles, ShieldCheck, Download } from "lucide-react";

function Row({ title, subtitle, items, testid }) {
  if (!items?.length) return null;
  return (
    <section className="container-page mt-20" data-testid={testid}>
      <div className="flex items-end justify-between mb-6 border-b border-border pb-3">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-1 italic">{subtitle}</p>}
        </div>
        <Link to="/browse" className="text-sm font-medium hover:text-accent flex items-center gap-1">
          See all <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Link>
      </div>
      <div className="product-grid">{items.map((p) => <ProductCard key={p.id} product={p} />)}</div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState({ featured: [], bestsellers: [], newest: [], top_rated: [] });
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products/featured").then((r) => setData(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.filter((c) => !c.parent_id))).catch(() => {});
  }, []);

  return (
    <>
      <section className="border-b border-border bg-[radial-gradient(ellipse_at_top,_hsl(36_30%_92%)_0%,_hsl(38_33%_96%)_60%)]" data-testid="home-hero">
        <div className="container-page py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-6">
              <Sparkles className="h-3.5 w-3.5 text-accent" strokeWidth={1.5} /> Independent press · est. 2026
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-balance">
              Stories that carry<br /> you home.
            </h1>
            <p className="mt-7 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A curated catalogue of digital ebooks &amp; templates from Soft Spine Press. Pay once, download forever, with your name quietly inked into every page.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 text-sm font-medium rounded-sm hover:opacity-90 transition-opacity tracking-wide uppercase">
                Browse catalogue <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/library" data-testid="hero-library" className="inline-flex items-center gap-2 border border-primary/30 text-primary px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-muted/60 transition-colors tracking-wide uppercase">
                My library
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg text-xs">
              <div className="flex items-start gap-2"><Download className="h-4 w-4 mt-0.5 text-accent" strokeWidth={1.5} /><span>Instant download</span></div>
              <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 mt-0.5 text-accent" strokeWidth={1.5} /><span>Personal watermark</span></div>
              <div className="flex items-start gap-2"><Sparkles className="h-4 w-4 mt-0.5 text-accent" strokeWidth={1.5} /><span>Lifetime re-downloads</span></div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-3 gap-3">
            {data.newest.slice(0, 6).map((p, idx) => (
              <Link to={`/product/${p.id}`} key={p.id} className={`aspect-[2/3] overflow-hidden rounded-sm border border-accent/30 bg-muted shadow-[0_12px_40px_-20px_rgba(58,31,71,0.4)] ${idx === 0 ? "col-span-2 row-span-2 aspect-auto" : ""}`}>
                <img loading="lazy" src={`${api.defaults.baseURL}/products/${p.id}/cover${p.cache_v ? `?v=${p.cache_v}` : ""}`} alt={p.title} className="object-cover w-full h-full hover:scale-105 transition-transform duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page mt-20" data-testid="home-categories">
        <div className="text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-muted-foreground ornament inline-block">The collection</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-3">Browse by category</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10 max-w-3xl mx-auto">
          {cats.map((c) => (
            <Link key={c.id} to={`/browse?category=${c.slug}`} data-testid={`home-cat-${c.slug}`}
              className="border border-border rounded-sm p-8 hover:border-accent transition-colors group bg-card text-center">
              <div className="font-display text-3xl tracking-tight">{c.name}</div>
              <div className="text-xs text-muted-foreground mt-2 uppercase tracking-[0.18em] group-hover:text-accent">Shop {c.name.toLowerCase()} →</div>
            </Link>
          ))}
        </div>
      </section>

      <Row title="Featured" subtitle="Curated by the press." items={data.featured} testid="row-featured" />
      <Row title="New releases" subtitle="Fresh off the press." items={data.newest} testid="row-newest" />
      <Row title="Bestsellers" subtitle="What readers are loving." items={data.bestsellers} testid="row-bestsellers" />

      <section className="container-page mt-20 mb-4" data-testid="home-club-cta">
        <div className="border border-accent/30 rounded-sm bg-card p-10 sm:p-14 grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-accent">Between The Lines</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-3">Quiet letters, sent monthly.</h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-lg">
              Join our readers&apos; club for a thoughtful note from the press, members-only offers, and the occasional free chapter sampler. One click to leave.
            </p>
          </div>
          <div className="md:col-span-2 text-center md:text-right">
            <Link to="/club" data-testid="home-club-link" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 text-sm font-medium rounded-sm hover:opacity-90 transition-opacity tracking-wide uppercase">
              Join the club
            </Link>
            <div className="text-xs text-muted-foreground mt-3 italic">Free · unsubscribe anytime</div>
          </div>
        </div>
      </section>
    </>
  );
}
